/**
 * useFrameSelection Hook
 * 
 * Centralized frame selection logic - single source of truth.
 * Ensures enablePanSelection is checked consistently across all triggers.
 * 
 * Features:
 * - Manages DirectHitStrategy instance
 * - Guards against selection during pan when enablePanSelection=false
 * - Validates jump distances
 * - Handles sticky selection, progressive weakening, hysteresis
 * - 🎬 Animates through intermediate frames when jump > 1
 * 
 * Usage:
 * ```tsx
 * const { selectFrame } = useFrameSelection()
 * 
 * // In pan handler
 * selectFrame(redDot, 'pan-move', imageDims, consecutiveAutoReturns)
 * 
 * // In auto-select effect
 * selectFrame(worldUnderRedDot, 'auto', imageDims)
 * ```
 */

import { useCallback, useRef, useEffect } from 'react'
import { useAppStore } from '@store/appStore'
import { useNavigationSettingsStore } from '@store/navigationSettingsSlice'
import { DirectHitStrategy } from '@services/DirectHitStrategy'
import { validateFrameJump, logJumpValidation } from '@utils/frameSelectionValidation'
import { getPanEndAnimationService } from '@services/frameAnimationService'
import { getAnimationConfig } from '../config/animationConfig'
import type { SelectionResult } from '@services/DirectHitStrategy'
import type { FrameFilterMetadata } from '@/types/NavigationSettings'
import type { OffsetMap } from '@/types/offsetMap'

/**
 * Build FrameFilterMetadata for all frames relative to a given current frame.
 * Extracts distance_3d and angle_diff from offsetMap all_pairs.
 * Only rebuilds when currentFrame changes (cached by caller).
 */
function buildFrameFilterMetadata(
  currentFrame: number,
  offsetMap: OffsetMap
): Map<number, FrameFilterMetadata> {
  const metadata = new Map<number, FrameFilterMetadata>()
  const prefix = `${currentFrame}_`
  const allPairs = offsetMap.all_pairs

  for (const key of Object.keys(allPairs)) {
    if (key.startsWith(prefix)) {
      const to = parseInt(key.substring(prefix.length), 10)
      const offset = allPairs[key]
      metadata.set(to, {
        frameIndex: to,
        distance3d: offset.distance_3d,
        angleDiff: offset.angle_diff,
        isConnected: true  // Pair exists → connected
      })
    }
  }

  return metadata
}

export function useFrameSelection() {
  const strategyRef = useRef<DirectHitStrategy | null>(null)
  const animationCancelRef = useRef<(() => void) | null>(null)
  // Cache frameMetadata per-frame to avoid rebuilding on every pan-move event
  const metadataCacheRef = useRef<{ frame: number; metadata: Map<number, FrameFilterMetadata> } | null>(null)
  
  // Initialize DirectHitStrategy when connectivity graph and settings are available
  useEffect(() => {
    const state = useAppStore.getState()
    const settings = useNavigationSettingsStore.getState().settings
    
    if (state.connectivityGraph && settings) {
      strategyRef.current = new DirectHitStrategy(state.connectivityGraph, settings)
      console.log('✅ [useFrameSelection] DirectHitStrategy initialized')
    }
  }, [])
  
  // Update strategy when settings change
  useEffect(() => {
    const unsubscribe = useNavigationSettingsStore.subscribe((state) => {
      if (strategyRef.current) {
        strategyRef.current.setNavigationSettings(state.settings)
      }
    })
    return unsubscribe
  }, [])

  // Update strategy when corridor direction map changes (from minimap_nav.json)
  useEffect(() => {
    let lastCorridorMap = useAppStore.getState().corridorDirectionByFrame
    // Set initial value
    if (lastCorridorMap && strategyRef.current) {
      strategyRef.current.setCorridorDirectionByFrame(lastCorridorMap)
    }
    const unsubscribe = useAppStore.subscribe((state) => {
      if (state.corridorDirectionByFrame !== lastCorridorMap) {
        lastCorridorMap = state.corridorDirectionByFrame
        if (strategyRef.current) {
          strategyRef.current.setCorridorDirectionByFrame(state.corridorDirectionByFrame)
        }
      }
    })
    return unsubscribe
  }, [])

  // 🎬 Cancel pan-end animation when setting is disabled
  useEffect(() => {
    const unsubscribe = useNavigationSettingsStore.subscribe((navState) => {
      if (!navState.settings.enablePanEndAnimation && animationCancelRef.current) {
        console.log(`🎬 [useFrameSelection] Pan-end animation disabled - cancelling ongoing animation`)
        animationCancelRef.current()
        animationCancelRef.current = null
        // Clear animation state in store
        useAppStore.getState().setAnimatingFrameIndex(null)
      }
    })
    return unsubscribe
  }, [])

  /**
   * Build array of intermediate frames between source and target
   */
  const buildIntermediateFrames = useCallback((sourceFrame: number, targetFrame: number): number[] => {
    const frames: number[] = []
    const direction = targetFrame > sourceFrame ? 1 : -1
    
    // Include intermediate frames (excluding source, INCLUDING target)
    for (let i = sourceFrame + direction; direction > 0 ? i <= targetFrame : i >= targetFrame; i += direction) {
      frames.push(i)
    }
    
    return frames
  }, [])

  /**
   * Select frame based on red dot position
   * 
   * This is the ONLY place where frame selection logic should be called from.
   * All triggers (pan-move, pan-end, auto-select) go through this function.
   * 
   * 🎬 When jump distance > 1, animates through intermediate frames before
   * selecting the final frame. Animation centers each frame on screen.
   * 
   * @param redDotPosition - World coordinates of red dot
   * @param trigger - What triggered the selection attempt
   * @param imageDimensions - Display dimensions for sticky bounds calculation
   * @param consecutiveAutoReturns - For progressive sticky weakening (default: 0)
   * @returns Selection result or null if blocked
   */
  const selectFrame = useCallback((
    redDotPosition: { worldX: number; worldY: number },
    trigger: 'pan-move' | 'pan-end' | 'auto',
    imageDimensions: { displayWidth: number; displayHeight: number } | undefined,
    consecutiveAutoReturns: number = 0,
    panMotionVector?: { x: number; y: number } | null,
    accumulatedPanDistance?: number  // 🆕 NEW: accumulated distance to prevent oscillation
  ): SelectionResult | null => {
    const state = useAppStore.getState()
    const settings = useNavigationSettingsStore.getState().settings
    
    // Guard: No strategy initialized
    if (!strategyRef.current) {
      console.log(`[FrameSelection] Blocked: Strategy not initialized`)
      return null
    }
    
    // Guard: No selected frame
    if (state.manuallySelectedFrameIndex === null || state.manuallySelectedFrameIndex === undefined) {
      return null
    }
    
    // Guard: ALWAYS block auto-select during pan to prevent oscillation/race with pan-move
    // const isPanning = state.isPanning
    
    // if (trigger === 'auto' && isPanning) {
    //   console.log(`[FrameSelection] Blocked: Auto-select during pan (prevents race with pan-move)`)
    //   return null
    // }
    
    // Guard: Check enablePanSelection for pan triggers
    // if (trigger === 'pan-move' && !settings.enablePanSelection) {
    //   console.log(`[FrameSelection] Blocked: Pan-move selection (enablePanSelection=false)`)
    //   return null
    // }
    
    if (trigger === 'pan-end' && !settings.enablePanSelection) {
      console.log(`[FrameSelection] Blocked: Pan-end selection (enablePanSelection=false)`)
      return null
    }
    
    // Use fallback dimensions if not provided
    const dims = imageDimensions || { 
      displayWidth: state.viewportSize.width, 
      displayHeight: state.viewportSize.height 
    }
    
    // Build frameMetadata from offsetMap (cached per current frame)
    let frameMetadata: Map<number, FrameFilterMetadata> | undefined
    if (state.offsetMap) {
      const currentFrame = state.manuallySelectedFrameIndex
      if (metadataCacheRef.current?.frame !== currentFrame) {
        metadataCacheRef.current = {
          frame: currentFrame,
          metadata: buildFrameFilterMetadata(currentFrame, state.offsetMap)
        }
      }
      frameMetadata = metadataCacheRef.current.metadata
    }
    
    // Call DirectHitStrategy with trigger parameter
    const result = strategyRef.current.selectFrameByRedDot(
      state.frameWorldPositions,
      redDotPosition,
      state.manuallySelectedFrameIndex,
      dims,
      frameMetadata,   // 3D distance, angle, connectivity data from offsetMap
      undefined, // restrictToConnected
      consecutiveAutoReturns,
      settings,
      trigger,  // Pass trigger so cooldown can be skipped for pan-end
      panMotionVector,  // 🆕 pass pan motion for directional selection
      accumulatedPanDistance  // 🆕 pass accumulated distance to prevent oscillation
    )
    
    // If no change, return early
    if (!result.hasChanged || result.selectedFrame === state.manuallySelectedFrameIndex) {
      return result
    }
    
    // Validate jump distance
    const dataset = state.availableDatasets.find(d => d.id === state.currentShopId)
    const reconstructionMethod = (dataset as any)?.reconstructionMethod as 'colmap' | 'phase_correlation' | undefined
    
    const jumpCheck = validateFrameJump(
      state.manuallySelectedFrameIndex,
      result.selectedFrame,
      reconstructionMethod,
      settings,
      state.offsetMap
    )
    
    if (!jumpCheck.allowed) {
      if (trigger !== 'pan-move') { // Don't spam logs during pan-move
        logJumpValidation(jumpCheck, `${trigger} selection`)
      }
      return null // Block the selection
    }
    
    // Log validation (except during pan-move to reduce noise)
    if (trigger !== 'pan-move') {
      logJumpValidation(jumpCheck, `${trigger} selection`)
    }
    
    // Calculate jump distance
    const sourceFrame = state.manuallySelectedFrameIndex
    const targetFrame = result.selectedFrame
    const jumpDistance = Math.abs(targetFrame - sourceFrame)
    
    // 🎬 ANIMATION: If jump > 1 AND trigger is pan-end AND animation is enabled
    // IMPORTANT: Only animate for pan-end trigger, NOT for pan-move
    // Pan-move selections should be instant (no animation) unless pan-move preview is used
    if (jumpDistance > 1 && trigger === 'pan-end' && settings.enablePanEndAnimation) {
      // Cancel any existing animation
      if (animationCancelRef.current) {
        animationCancelRef.current()
        animationCancelRef.current = null
      }
      
      const intermediateFrames = buildIntermediateFrames(sourceFrame, targetFrame)
      
      console.log(`🎬 [FrameSelection] Animation triggered: ${sourceFrame} → ${targetFrame} (jump: ${jumpDistance})`)
      console.log(`   Intermediate frames: [${intermediateFrames.join(', ')}]`)
      
      const animationService = getPanEndAnimationService()
      const config = getAnimationConfig('PAN_END_SMOOTH')
      
      if (config && intermediateFrames.length > 0) {
        // Set animating state in store for rendering
        useAppStore.getState().setAnimatingFrameIndex(intermediateFrames[0])
        
        animationCancelRef.current = animationService.animateFrameSequence(
          intermediateFrames,
          targetFrame,
          config,
          {
            onFrameShow: (frameIndex: number) => {
              console.log(`🎬 [FrameSelection] Animation frame: ${frameIndex}`)
              // Get fresh state for each callback
              const freshState = useAppStore.getState()
              // Update animating frame for rendering
              freshState.setAnimatingFrameIndex(frameIndex)
              // 🎯 Center viewport on this frame (holy red dot centering) - SKIP AUTO-SELECT during animation
              freshState.centerViewportOnFrame(frameIndex, undefined, undefined, { skipAutoSelect: true })
            },
            onComplete: (finalFrame: number) => {
              console.log(`✅ [FrameSelection] Animation complete, selecting frame: ${finalFrame}`)
              // Get fresh state for completion
              const freshState = useAppStore.getState()
              // Clear animation state
              freshState.setAnimatingFrameIndex(null)
              animationCancelRef.current = null
              // Select the final frame (history recording happens in setManuallySelectedFrame)
              freshState.setManuallySelectedFrame(finalFrame)
              // 🎯 Center viewport on final frame (allow auto-select to work again)
              freshState.centerViewportOnFrame(finalFrame)
            }
          }
        )
        
        // Return the result but don't apply selection yet (animation will do it)
        return result
      }
    }
    
    // No animation needed (jump <= 1), apply selection directly
    console.log(`[FrameSelection] Applying selection: ${sourceFrame} → ${targetFrame} (trigger: ${trigger})`)
    // History recording happens in setManuallySelectedFrame
    state.setManuallySelectedFrame(targetFrame)
    
    return result
  }, [buildIntermediateFrames])

  return { selectFrame, strategyRef }
}
